import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Table from 'apollo-react/components/Table';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ActionCell, NameCell, ValueCell } from './columns';
import { ExpandableComponent } from './ExpandableComponent';
import { useFormSelector } from '../../../../../custom-hooks';
import { DATALOOKUP_TYPE_EXTERNAL_API } from '../../../../constants/form-manager.constants';
import { getTranslations } from '../../../../selectors/common.selectors';
import { getApiConfigurationVariables } from '../../../../selectors/editor.selectors';
import { getDataSourceFieldList, modifyVariables } from '../../../../utils/editor.utils';

const styles = {
  root: {
    overflow: 'hidden',
    '& [class*="Table-tableWrapper"]': {
      overflow: 'hidden',
    },
    '& [class*="MuiToolbar-root"]': {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    '& [class*="MuiToolbar-root"] div': {
      paddingRight: 20,
    },
    '& [class*="MuiTableHead-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="MuiTableBody-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="InternalTableBody-editMode"]': {
      backgroundColor: 'white',
      borderTop: 'none !important',
      borderBottom: 'none !important',
    },
    '& [class*="EmptyRow-middleAlign"]': {
      height: '90px !important',
    },

    '& [class*="EmptyRow-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
      minHeight: '239px',
    },
    '& [class*="MuiFormControl-marginNormal"]': {
      marginTop: '0px',
    },
  },
  rootFormulaEditorContainer: {
    flex: 1,
    marginBottom: 5,
  },
};

const columns = [
  {
    header: 'Name',
    accessor: 'Name',
    customCell: NameCell,
  },
  {
    header: 'Value',
    accessor: 'Value',
    customCell: ValueCell,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

const QueryParamsModal = ({
  name,
  controlId,
  currentControlModel,
  currentQueryParams,
  setCurrentQueryParams,
}) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [rows, setRows] = useState([]);
  const [fieldList, setFieldList] = useState([]);
  const [expandedRow, setExpandedRows] = useState([]);
  const [currentKeyTemp, setCurrentKeyTemp] = useState('');

  const translations = useFormSelector(getTranslations);
  const outboundVariables = useFormSelector(getApiConfigurationVariables, {
    configurationName: currentControlModel.dataObject,
    dataLookUpType: currentControlModel.dataLookUpType,
    field: 'outboundVariables',
  });
  const propValue = currentQueryParams;

  useEffect(() => {
    if (currentControlModel.dataLookUpType === DATALOOKUP_TYPE_EXTERNAL_API) {
      setFieldList(modifyVariables(outboundVariables));
    } else {
      getDataSourceFieldList({
        dataLookupType: currentControlModel.dataLookUpType,
        dataObject: currentControlModel.dataObject,
      }).then((res) => {
        setFieldList(res);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRows(propValue ?? []);
  }, [propValue]);

  const addRow = () => {
    const rowId = `${uuidv4()}`;
    const newQueryParam = {
      id: rowId,
      Name: '',
      Value: '',
      Type: 'formula',
    };
    const newRows = [...rows, newQueryParam];
    setRows(newRows);
    setCurrentQueryParams(newRows);
  };

  const CustomHeader = () => {
    const buttonText = 'Add New Param';
    return (
      <div>
        <Button variant="text" size="small" onClick={addRow}>
          {buttonText}
        </Button>
      </div>
    );
  };

  const updateLocalState = (newRowsTemp) => {
    const newRows = newRowsTemp || rows;
    if (controlId && name) {
      setCurrentQueryParams(newRows);
      setRows(newRows);
    }
  };

  const editRow = (id, key, value) => {
    const currentRows = rows.map((row) => (row.id === id ? { ...row, [key]: value } : row));
    setRows(currentRows);
    updateLocalState(currentRows);
  };

  const deleteRow = (id) => {
    const newRows = rows.filter((row) => row.id !== id);
    updateLocalState(newRows);
  };

  const handleToggleRow = (Id, currentKeyTemp) => {
    setCurrentKeyTemp(currentKeyTemp);
    setExpandedRows((expandedRows) =>
      expandedRows.includes(Id)
        ? expandedRows.filter((id) => id !== Id)
        : [...expandedRows, Id, currentKeyTemp]
    );
  };

  return (
    <div className={classes.root}>
      <Table
        title="Query Params"
        columns={columns}
        rows={rows.map((row) => ({
          ...row,
          rows,
          key: row.id,
          currentKeyTemp,
          dataLookUpType: currentControlModel.dataLookUpType,
          expanded: expandedRow.includes(row.id),
          translations: translations.Entity,
          editRow,
          deleteRow,
          handleToggleRow,
          classes,
          fieldList,
        }))}
        initialSortOrder="asc"
        hidePagination
        CustomHeader={CustomHeader}
        ExpandableComponent={ExpandableComponent}
      />
    </div>
  );
};

export default QueryParamsModal;
